import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
// WordPress CMS fetch is disabled — the site is served entirely by local
// components. Re-enable this import alongside Strategy 2 below if needed.
// import { fetchWordPressPage } from '../utils/wpPages';
import { blogSlug } from '../config';
import { LoadingSpinner } from './LoadingSpinner';

/**
 * WordPress Page data structure
 * 
 * Represents a WordPress page with minimal required fields for rendering.
 * Note: Content is pre-rendered HTML from WordPress.
 */
interface WordPressPage {
  /** Unique page identifier */
  id: number;
  /** Page title (HTML rendered) */
  title: { rendered: string };
  /** Page content (HTML rendered) */
  content: { rendered: string };
}

/**
 * Local component mapping for dynamic imports
 * 
 * Maps route slugs to their corresponding React components.
 * Components are loaded dynamically to enable code splitting.
 * 
 * @example
 * - 'home' -> Home component
 * - 'about' -> About component
 * - blogSlug -> Blog component (dynamic based on config)
 */
const localComponents: { [key: string]: () => Promise<any> } = {
  home: () => import('../pages/Home'),
  about: () => import('../pages/About'),
  [blogSlug]: () => import('../pages/Blog'),
};

/**
 * Dynamic Page Component
 * 
 * Handles both WordPress CMS pages and local React components.
 * Implements a fallback strategy:
 * 1. Try to fetch WordPress page by slug
 * 2. Fall back to local React component if available
 * 3. Show 404 if neither exists
 * 
 * @returns {JSX.Element} The rendered page component
 */
const DynamicPage = () => {
  // Extract slug from URL parameters, default to 'home'
  const { slug = 'home' } = useParams<{ slug: string }>();
  
  // State management for different page types and loading states
  const [wpPage, setWpPage] = useState<WordPressPage | null>(null);
  const [LocalComponent, setLocalComponent] = useState<React.ComponentType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  /**
   * Load page content based on slug
   * 
   * Implements the following loading strategy:
   * 1. Reset all state to ensure clean loading
   * 2. Try WordPress page first (CMS content)
   * 3. Fall back to local React component
   * 4. Show 404 if nothing found
   * 
   * Special case: 'home' slug always loads local Home component as fallback
   */
  useEffect(() => {
    const loadPage = async () => {
      // Reset all state for clean loading
      setIsLoading(true);
      setNotFound(false);
      setWpPage(null);
      setLocalComponent(null);

      try {
        // Strategy 1: Prefer a local React component when one exists for this
        // slug. These pages (home, about, blog) are self-contained and do NOT
        // use WordPress content, so we must NOT block their first paint on a
        // network round-trip to the CMS. Doing so previously added seconds of
        // spinner before the hero could render.
        const componentLoader = localComponents[slug];
        if (componentLoader) {
          const module = await componentLoader();
          setLocalComponent(() => module.default);
          return;
        }

        // Strategy 2: (DISABLED) Fetch the WordPress page for this slug.
        // The KimRose site is fully served by local React components, so the
        // CMS round-trip is disabled to avoid any network wait before render.
        // Re-enable if WordPress-backed pages are ever added.
        // const page = await fetchWordPressPage(slug);
        // if (page) {
        //   setWpPage(page);
        //   return;
        // }

        // Strategy 3: If no local component matched, show 404
        setNotFound(true);
      } catch (error) {
        console.error('Error loading page:', error);
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadPage();
  }, [slug]); // Re-run when slug changes

  // Loading state: Show spinner while fetching content
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Error state: Show 404 when no content found
  if (notFound) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Page Not Found</h1>
          <p className="text-gray-600 mb-4">The page you're looking for doesn't exist.</p>
          <a href="/" className="text-blue-600 hover:text-blue-800">
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  // Success state: Render local React component
  if (LocalComponent) {
    return <LocalComponent />;
  }

  // Success state: Render WordPress page content
  // WARNING: Using dangerouslySetInnerHTML - content should be sanitized
  // TODO: Implement DOMPurify or similar sanitization library
  if (wpPage) {
    return (
      <div className="container mx-auto px-4 py-8">
        <article className="max-w-4xl mx-auto">
          <h1 
            className="text-4xl font-bold mb-8"
            dangerouslySetInnerHTML={{ __html: wpPage.title.rendered }}
          />
          <div 
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: wpPage.content.rendered }}
          />
        </article>
      </div>
    );
  }

  // Fallback: Should never reach here due to loading logic
  return null;
};

export default DynamicPage; 